import React from "react";
import type { ClassificationResult } from "../types/classificationResult";

interface ValidationGateBadgeProps {
  result: Pick<ClassificationResult, "status" | "hs_code" | "validatorGate">;
}

export function isFinalGateEligible(
  result: Pick<ClassificationResult, "status" | "hs_code" | "validatorGate">,
): boolean {
  const hasCode = Boolean(result.hs_code && result.hs_code !== "UNKNOWN");
  const responseIsFinal =
    result.status === "FINAL" || result.status === "FINAL_WITH_WARNING";
  if (!hasCode || !responseIsFinal) {
    return false;
  }

  const gate = result.validatorGate;
  if (!gate) return false;

  const legalChainOk = !gate.legal_chain || gate.legal_chain.status === "OK";
  const finalEligibilityOk = gate.final_eligibility?.eligible === true;
  return gate.final_status === "FINAL" && legalChainOk && finalEligibilityOk;
}

export function ValidationGateBadge({ result }: ValidationGateBadgeProps): React.ReactElement {
  const gate = result.validatorGate;
  const finalEligible = isFinalGateEligible(result);
  const status = gate?.final_status ?? result.status;
  const className = [
    "validation-gate-badge",
    finalEligible ? "validation-gate-badge-final" : "",
    status === "STOP" ? "validation-gate-badge-stop" : "",
    status === "NEEDS_INFO" ? "validation-gate-badge-needs-info" : "",
  ].filter(Boolean).join(" ");

  const label = finalEligible
    ? "FINAL eligible"
    : status === "STOP"
      ? "STOP - no final classification"
      : status === "NEEDS_INFO"
        ? "NEEDS_INFO - more facts required"
        : "Audit gate not final";

  return (
    <span className={className} data-testid="validation-gate-badge">
      {label}
      {gate?.data_quality_fallback ? " | data-quality fallback" : ""}
    </span>
  );
}

export default ValidationGateBadge;
